import { i18n } from "@lingui/core";
import { Brand, Subvertical } from "../constants.js";
import { eventReference } from "../models/trackingEvent.js";

export enum ToolType {
    CONTRACT = "contract",
    PRICE_VALUATION = "price_valuation",
    SMIDIG_BIL = "smidig_bil",
    SALES = "sales",
}

export enum ToolRoute {
    PRICE_EVALUATION = "/mobility/insights/price-valuation",
    SMIDIG_BILHANDEL = "/motor/artikler/aktuelt/smidig-bilhandel",
    SELL_CAR = "/selg-bil",
    BLOCKET_CAR_INSURANCE = "https://www.blocket.se/fordon/forsakringar/bestall-trygghansa-bilforsakring",
    DBA_CAR_WORTH = "https://www.bilbasen.dk/bilvurdering",
    NEW_CAR_TESTS = "/bilguiden/tester-og-anmeldelser",
}

export interface ToolItem {
    category: string;
    header: string;
    body?: string;
    route: ToolRoute | string;
    trackingNumber: string;
    placement?: number;
    type?: ToolType;
    imageUrl?: string;
    icon?: string;
    iconBackground?: string;
}

const TOOL_TYPE_IMAGE_URL: Record<ToolType, string> = {
    [ToolType.CONTRACT]: "/editorial-content/tools/contract.png",
    [ToolType.PRICE_VALUATION]: "/editorial-content/tools/price.png",
    [ToolType.SMIDIG_BIL]: "/editorial-content/tools/smidig.png",
    [ToolType.SALES]: "/editorial-content/tools/sales.png",
};

export const getToolImageUrl = (type: ToolType | undefined): string | undefined => {
    if (!type) return undefined;
    return TOOL_TYPE_IMAGE_URL[type];
};

export const SHARED_TOOLS_CONFIG = (): Record<Brand, Record<Subvertical, ToolItem[]>> => ({
    finn: {
        car: [
            {
                category: "Tools",
                header: i18n.t({
                    id: "market-frontpage.price-advice",
                    message: "Used car price tips",
                }),
                body: i18n.t({
                    id: "market-frontpage.price-advice-description",
                    message: "Get price estimates from similar cars sold on FINN",
                }),
                route: ToolRoute.PRICE_EVALUATION,
                trackingNumber: eventReference.PRICE_TIPS,
                placement: 1,
                type: ToolType.PRICE_VALUATION,
                icon: "Money",
                iconBackground: "s-bg-warning-subtle",
            },
            {
                category: "Tools",
                header: i18n.t({
                    id: "market-frontpage.smidig-bilhandel",
                    message: "Smooth car trade",
                }),
                body: i18n.t({
                    id: "market-frontpage.smidig-bilhandel-description",
                    message: "Safe and easy car trade with Smidig: payment, ownership transfer and contract.",
                }),
                route: ToolRoute.SMIDIG_BILHANDEL,
                trackingNumber: eventReference.CAR_TRADING,
                placement: 2,
                type: ToolType.SMIDIG_BIL,
                icon: "Warranty",
                iconBackground: "s-bg-primary-subtle",
            },
            {
                category: "Tools",
                header: i18n.t({
                    id: "market-frontpage.new-car-tests",
                    message: "New car tests",
                }),
                body: i18n.t({
                    id: "market-frontpage.new-car-tests-description",
                    message: "We help you compare before you decide.",
                }),
                route: ToolRoute.NEW_CAR_TESTS,
                trackingNumber: eventReference.NEW_CAR_TESTS,
                placement: 3,
                type: ToolType.SALES,
                icon: "Car",
                iconBackground: "s-bg-positive-subtle",
            },
        ],
        boat: [],
        mc: [],
        b2b: [],
        caravan: [],
    },
    blocket: {
        car: [
            {
                category: "Tools",
                header: i18n.t({
                    id: "market-frontpage.price-advice",
                    message: "Used car price tips",
                }),
                body: i18n.t({
                    id: "market-frontpage.price-advice-description",
                    message: "Get price estimates from similar cars sold on FINN",
                }),
                route: ToolRoute.PRICE_EVALUATION,
                trackingNumber: eventReference.PRICE_TIPS,
                placement: 1,
                type: ToolType.PRICE_VALUATION,
                imageUrl: "/editorial-content/tools/price-blocket.png",
                icon: "CarIllustration",
                iconBackground: "",
            },
            {
                category: "Tools",
                header: i18n.t({
                    id: "market-frontpage.blocket-car-insurance",
                    message: "7 days free insurance",
                }),
                body: i18n.t({
                    id: "market-frontpage.blocket-car-insurance-description",
                    message: "Protect yourself and your new car from the first mile. Pre-order 7 days of insurance at no cost.",
                }),
                route: ToolRoute.BLOCKET_CAR_INSURANCE,
                trackingNumber: eventReference.CONTRACT_OF_SALE,
                placement: 2,
                type: ToolType.CONTRACT,
                icon: "ContractIllustration",
                iconBackground: "",
            },
        ],
        boat: [],
        mc: [],
        b2b: [],
        caravan: [],
    },
    dba: {
        car: [
            {
                category: "Tools",
                header: i18n.t({
                    id: "market-frontpage.car-worth-title",
                    message: "What is my car worth?",
                }),
                body: i18n.t({
                    id: "market-frontpage.car-worth-description",
                    message:
                        "Find out what your car is worth on our sister site, Bilbasen. Enter your registration number and get a guide price – based on thousands of current transactions.",
                }),
                route: ToolRoute.DBA_CAR_WORTH,
                trackingNumber: eventReference.CAR_WORTH,
                placement: 1,
                type: ToolType.PRICE_VALUATION,
                icon: "Calculator",
                iconBackground: "s-bg-warning-subtle",
            },
        ],
        boat: [],
        mc: [],
        b2b: [],
        caravan: [],
    },
});

export const getFullUrl = (route: string, brand: Brand): string => {
    if (route.startsWith("http://") || route.startsWith("https://")) {
        return route;
    }

    switch (brand) {
        case Brand.FINN:
            return `https://www.finn.no${route}`;
        case Brand.BLOCKET:
            return `https://www.blocket.se${route}`;
        case Brand.DBA:
            return `https://www.dba.dk${route}`;
        default:
            return route;
    }
};
